import { CommonLaboratoryResponse, CommonProtocolResponse } from '@/api/services/common.dto'
import { CommonAssayResponse } from '@/api/services/common/assay/common-assay.dto'
import { CommonAssaygroupResponse } from '@/api/services/common/assaygroup/common-assaygroup.dto'
import { CommonDeviceResponse } from '@/api/services/common/device/common-device.dto'
import { CommonPersonnelResponse } from '@/api/services/common/personnel/common-personnel.dto'
import { CommonPriorityResponse } from '@/api/services/common/priority/common-priority.dto'
import { CommonStatusResponse } from '@/api/services/common/status/common-status.dto'
import { CommonSubportionTypeResponse } from '@/api/services/common/subportion-type/common-subportion-type.dto'
import i18n from '@/lang/i18n'
import { StatisticalAnalysisExternalListCase, StatisticalAnalysisExternalListColumnsCases } from '@/modules/statistical-analysis-module/shared/external-list-columns/external-list-columns-cases'
import { StatisticalAnalysisExternalListColumnsPortions, StatisticalAnalysisExternalListPortion } from '@/modules/statistical-analysis-module/shared/external-list-columns/external-list-columns-portions'
import { StatisticalAnalysisExternalListColumnsSample, StatisticalAnalysisExternalListSample } from '@/modules/statistical-analysis-module/shared/external-list-columns/external-list-columns-samples'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import { ColDef, ColGroupDef } from '@ag-grid-enterprise/all-modules'

export interface StatisticalAnalysisExternalListSubportion {
    assay: CommonAssayResponse
    assayGroup: CommonAssaygroupResponse
    caseStudy: StatisticalAnalysisExternalListCase
    cutDate: string
    cutTechnician: CommonPersonnelResponse
    cuttingDevice: CommonDeviceResponse
    deliverDate: string
    deliverPersonnel: CommonPersonnelResponse
    hasAttachments: boolean
    hasIncidents: boolean
    hasRevisions: boolean
    portion: StatisticalAnalysisExternalListPortion
    priority: CommonPriorityResponse
    protocol: CommonProtocolResponse
    requestDate: string
    requestResponsible: CommonPersonnelResponse
    sample: StatisticalAnalysisExternalListSample
    scanEndDate: string
    scanEndPersonnel: CommonPersonnelResponse
    scanStartDate: string
    scanStartPersonnel: CommonPersonnelResponse
    stainEndDate: string
    stainEndPersonnel: CommonPersonnelResponse
    stainStartDate: string
    stainStartPersonnel: CommonPersonnelResponse
    state: CommonStatusResponse
    subportionId: number
    subportionLabel: string
    subportionNumber: string
    subportionType: CommonSubportionTypeResponse,
    laboratory: CommonLaboratoryResponse
}

export const StatisticalAnalysisExternalListColumnsSubportions = ():ExportedColDef[] => {
    return [
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.subportionId'),
            field: 'subportionLabel',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.subportionType'),
            field: 'subportionType.sptName',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.requestDate'),
            field: 'requestDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.cutDate'),
            field: 'cutDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            hide: true,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.stainStartDate'),
            field: 'stainStartDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            hide: true,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.stainEndDate'),
            field: 'stainEndDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.scanStartDate'),
            field: 'scanStartDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            hide: true,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.scanEndDate'),
            field: 'scanEndDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.deliverDate'),
            field: 'deliverDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.cutTechnician'),
            field: 'cutTechnician.fullName',
            hide: true,
            exportType: AgGridExportType.TEXT,
            minWidth: 120,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.stainStartPersonnel'),
            field: 'stainStartPersonnel.fullName',
            hide: true,
            exportType: AgGridExportType.TEXT,
            minWidth: 120,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.stainEndPersonnel'),
            field: 'stainEndPersonnel.fullName',
            exportType: AgGridExportType.TEXT,
            minWidth: 120,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.cuttingDevice'),
            field: 'cuttingDevice.device',
            exportType: AgGridExportType.TEXT,
            minWidth: 120,
            flex: 1,
            hide: true,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.scanStartPersonnel'),
            field: 'scanStartPersonnel.fullName',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            hide: true,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.scanEndPersonnel'),
            field: 'scanEndPersonnel.fullName',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.deliverPersonnel'),
            field: 'deliverPersonnel.fullName',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.COMMON_SUBPORTION_STATUS,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.state'),
            field: 'state',
            exportField: 'state.name',
            exportType: AgGridExportType.TEXT,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.protocol'),
            field: 'protocol.prtName',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.assay'),
            field: 'assay.name',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.assayGroup'),
            field: 'assayGroup.name',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.PRIORITY,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.priority'),
            field: 'priority',
            exportField: 'priority.name',
            exportType: AgGridExportType.TEXT,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.requestResponsible'),
            field: 'requestResponsible.fullName',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.hasRevisions'),
            field: 'hasRevisions',
            exportType: AgGridExportType.BOOLEAN,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.hasIncidents'),
            field: 'hasIncidents',
            exportType: AgGridExportType.BOOLEAN,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.hasAttachments'),
            field: 'hasAttachments',
            exportType: AgGridExportType.BOOLEAN,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        }
    ]
}

export const StatisticalAnalysisExternalListSubportions = (): (ColDef[] | ColGroupDef[] | ExportedColGroupDef[] | ExportedColDef []) => {
    return [
        {
            headerName: i18n.tc('statisticalAnalysis.grids.groupCases'),
            children: StatisticalAnalysisExternalListColumnsCases().map((item:ExportedColDef) => {
                item.field = `caseStudy.${item.field}`
                if (item.exportField) {
                    item.exportField = `caseStudy.${item.exportField}`
                }
                return item
            })
        },
        {
            headerName: i18n.tc('statisticalAnalysis.grids.groupSamples'),
            children: StatisticalAnalysisExternalListColumnsSample().map((item:ExportedColDef) => {
                item.field = `sample.${item.field}`
                if (item.exportField) {
                    item.exportField = `sample.${item.exportField}`
                }
                return item
            })
        },
        {
            headerName: i18n.tc('statisticalAnalysis.grids.portion.groupPortions'),
            children: StatisticalAnalysisExternalListColumnsPortions().map((item:ExportedColDef) => {
                item.field = `portion.${item.field}`
                if (item.exportField) {
                    item.exportField = `portion.${item.exportField}`
                }
                return item
            })
        },
        {
            headerName: i18n.tc('statisticalAnalysis.grids.subportion.groupSubportions'),
            children: StatisticalAnalysisExternalListColumnsSubportions()
        }
    ]
}
